import Container from "../../components/Container";
import Show from "../../components/Show";
import Text from "../../components/Text";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import Icon from "../../components/Icon";
import { Skeleton } from "primereact/skeleton";
import moment from "moment";
import { Margin, usePDF } from "react-to-pdf";
import React, { useRef, useState } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import { Chip } from "primereact/chip";
import { InputMask } from "primereact/inputmask";
import Label from "../../components/Label";

interface IAnalysisContainerProps {
    loading: boolean;
    defaultDates?: Date[];
    period?: Date[];
    setPeriod?: (period: Date[]) => void;
    children: React.ReactNode;
    showExport?: { filename: string };
    filters?: { name: string; content: React.ReactNode; chip?: { label: string; icon: string; }; }[];
}

const AnalysisContainer = ({ loading, period, defaultDates, setPeriod, showExport, children, filters }: IAnalysisContainerProps) => {
    const { toPDF, targetRef } = usePDF({ filename: `${showExport?.filename}.pdf` });
    const periodPanelRef = useRef<OverlayPanel|null>(null);
    const filtersPanelRef = useRef<(OverlayPanel|null)[]>([]);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    
    return (
        <Container flexColumn fullWidth gap={2}>
            <Container wrap fullWidth gap={3} className="flex justify-content-between align-items-end">
                <Container wrap gap={2}>
                    <Show when={!!period}>
                        <Container flexColumn wrap gap={2}>
                            <Container>
                                <Button
                                    className="bg-transparent border-0 border-transparent gap-2 p-0"
                                    onClick={(e) => periodPanelRef?.current?.toggle(e)}
                                >
                                    <Show when={!!period?.[0] && !!period?.[1]} fallback={<Chip label="Desde o início" icon="pi pi-calendar" className="text-xs" />} >
                                        <Chip label={`${moment(period?.[0]).format("DD/MM/YYYY")} à ${moment(period?.[1]).format("DD/MM/YYYY")}`} icon="pi pi-calendar" className="text-xs" />
                                    </Show>
                                </Button>

                                <OverlayPanel ref={periodPanelRef}>
                                    <Container flexColumn gap={3} className="w-full">
                                        <Container wrap gap={2} className="align-items-center">
                                            <Button 
                                                outlined
                                                size="small"
                                                className="flex-1 justify-content-center"
                                                onClick={() => {
                                                    setPeriod?.([]);
                                                    setStartDate(null);
                                                    setEndDate(null);
                                                }}
                                            >
                                                Desde o início
                                            </Button>

                                            <Button
                                                outlined
                                                size="small"
                                                className="flex-1 justify-content-center"
                                                onClick={() => {
                                                    setPeriod?.([moment().subtract(1, "month").toDate(), new Date()]);
                                                    setStartDate(moment().subtract(1, "month").toDate());
                                                    setEndDate(new Date());
                                                }}
                                            >
                                                Último mês
                                            </Button>

                                            <Button
                                                outlined
                                                size="small"
                                                className="flex-1 justify-content-center"
                                                onClick={() => {
                                                    setPeriod?.([moment().subtract(3, "month").toDate(), new Date()]);
                                                    setStartDate(moment().subtract(3, "month").toDate());
                                                    setEndDate(new Date());
                                                }}
                                            >
                                                Últimos 3 meses
                                            </Button>
                                        </Container>

                                        <Container wrap gap={2} className="align-items-center">
                                            <Button
                                                outlined
                                                size="small"
                                                className="flex-1 justify-content-center"
                                                onClick={() => {
                                                    setPeriod?.([moment().subtract(6, "month").toDate(), new Date()]);
                                                    setStartDate(moment().subtract(6, "month").toDate());
                                                    setEndDate(new Date());
                                                }}
                                            >
                                                Últimos 6 meses
                                            </Button>

                                            <Button
                                                outlined
                                                size="small"
                                                className="flex-1 justify-content-center"
                                                onClick={() => {
                                                    setPeriod?.([moment().subtract(1, "year").toDate(), new Date()]);
                                                    setStartDate(moment().subtract(1, "year").toDate());
                                                    setEndDate(new Date());
                                                }}
                                            >
                                                Último ano
                                            </Button>

                                            <Button
                                                outlined
                                                size="small"
                                                className="flex-1 justify-content-center"
                                                onClick={() => {
                                                    setPeriod?.([moment().subtract(2, "year").toDate(), new Date()]);
                                                    setStartDate(moment().subtract(2, "year").toDate());
                                                    setEndDate(new Date());
                                                }}
                                            >
                                                Últimos 2 anos
                                            </Button>
                                        </Container>

                                        <Container gap={2} className="align-items-center">
                                            <InputMask 
                                                value={startDate ? moment(startDate).format("DD/MM/YYYY") : null}
                                                mask="99/99/9999"
                                                placeholder="Data inicial"
                                                onChange={(e) => {
                                                    const [day, month, year] = (e.value ?? "").split('/').map(Number);
                                                    if (day <= 31 && month <= 12 && year >= 1900 && year <= 2999) {
                                                        setPeriod?.([moment(e.value, "DD/MM/YYYY").toDate(), period?.[1] ?? new Date()]);
                                                        setStartDate(moment(e.value, "DD/MM/YYYY").toDate());
                                                    } else if (day > 31 && month > 12 && (year < 1900 || year > 2999)) {
                                                        setPeriod?.([new Date(), period?.[1] ?? new Date()]);
                                                        setStartDate(new Date());
                                                    }
                                                }}
                                            />

                                            <Label>até</Label>

                                            <InputMask 
                                                value={endDate ? moment(endDate).format("DD/MM/YYYY") : null}
                                                mask="99/99/9999"
                                                placeholder="Data final"
                                                onChange={(e) => {
                                                    const [day, month, year] = (e.value ?? "").split('/').map(Number);
                                                    if (day <= 31 && month <= 12 && year >= 1900 && year <= 2999) {
                                                        setPeriod?.([period?.[0] ?? new Date(), moment(e.value, "DD/MM/YYYY").toDate()]);
                                                        setEndDate(moment(e.value, "DD/MM/YYYY").toDate());
                                                    } else if (day > 31 && month > 12 && (year < 1900 || year > 2999)) {
                                                        setPeriod?.([period?.[0] ?? new Date(), new Date()]);
                                                        setEndDate(new Date());
                                                    }
                                                }}
                                            />
                                        </Container>

                                        <Calendar
                                            value={period}
                                            selectionMode="range"
                                            onChange={(e) => setPeriod?.((e.value ?? defaultDates!).filter((date): date is Date => date !== null))}
                                            inline
                                            className="w-full"
                                            locale="pt"
                                        />
                                    </Container>
                                </OverlayPanel>
                            </Container>
                        </Container>
                    </Show>

                    {filters?.map(filter => {
                        return (
                            <Container key={filter.name} flexColumn wrap gap={2} className="justify-content-end">
                                <Container>
                                    <Button
                                        className="bg-transparent border-0 border-transparent gap-2 p-0"
                                        onClick={(e) => filtersPanelRef.current[filters.indexOf(filter)]?.toggle(e)}
                                    >
                                        <Chip label={!filter.chip?.label ? filter.name : filter.chip.label} icon={`pi ${filter.chip?.icon}`} className="text-xs" />
                                    </Button>

                                    <OverlayPanel ref={(el) => filtersPanelRef.current.push(el ?? null)}>
                                        {filter.content}
                                    </OverlayPanel>
                                </Container>
                            </Container>
                        )
                    })}
                </Container>

                <Container wrap>
                    <Show when={!!showExport?.filename}>
                        <Button
                            className="bg-transparent border-0 border-transparent gap-2"
                            onClick={() => toPDF({ page: { margin: Margin.MEDIUM } })}
                        >
                            <Container flexColumn>
                                <Icon name="pi pi-file-pdf" className="text-700" />
                            </Container>

                            <Text>Exportar</Text>
                        </Button>
                    </Show>
                </Container>
            </Container>

            <Show
                when={!loading}
                fallback={
                    <Container flexColumn gap={3} fullWidth>
                        <Container wrap gap={3} fullWidth>
                            <Skeleton height="90px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                            <Skeleton height="90px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                            <Skeleton height="90px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                        </Container>

                        <Container wrap gap={3} fullWidth>
                            <Skeleton height="300px" className="flex border-1 bg-gray-200 border-gray-300 flex-1" />
                        </Container>
                    </Container>
                }
            >
                <div ref={targetRef}>
                    {children}
                </div>
            </Show>
        </Container>
    );
}

export default AnalysisContainer;
